<template>
  <form @submit.prevent="handleSubmit" id="material_edit_form" enctype="multipart/form-data">
    <div v-if="initialLoading" class="text-center py-5">
      <i class="fa fa-spinner fa-spin fs-3 text-primary"></i>
      <div class="text-muted mt-2">Loading material details...</div>
    </div>

    <template v-else>
      <div class="mb-4">
        <label class="form-label form-label-thin">Material Name <span class="text-danger">*</span></label>
        <input type="text" class="form-control form-control-thin" ref="name" id="name" name="name" v-model="form.name"
          placeholder="Material Name" />
        <div v-if="errors.name" class="text-danger small">{{ errors.name[0] }}</div>
      </div>
      <div class="mb-4">
        <label class="form-label form-label-thin">Code <span class="text-danger">*</span></label>
        <input type="text" class="form-control form-control-thin" id="code" name="code" ref="code" v-model="form.code"
          placeholder="Material Code" />
        <div v-if="errors.code" class="text-danger small">{{ errors.code[0] }}</div>
      </div>
      <div class="mb-4">
        <label class="form-label form-label-thin">Serial Number <span class="text-danger">*</span></label>
        <input type="text" class="form-control form-control-thin" id="serial_number" ref="serial_number"
          name="serial_number" v-model="form.serial_number" placeholder="Serial Number" />
        <div v-if="errors.serial_number" class="text-danger small">{{ errors.serial_number[0] }}</div>
      </div>
      <div class="mb-4">
        <MultiSelect v-model="form.material_category_id" :multiple="false" label="Material Category"
          :options="materialCategories" option-label="name" option-value="id" id="material_category_id"
          name="material_category_id" ref="material_category_id" />
      </div>

      <div class="mb-4">
        <label class="form-label form-label-thin">
          Manufacturer <span class="text-danger">*</span>
        </label>

        <input type="text" class="form-control form-control-thin" id="manufacturer" name="manufacturer"
          ref="manufacturer" v-model="form.manufacturer" placeholder="Manufacturer" />
      </div>

      <div class="mb-4">
        <label class="form-label form-label-thin">
          Unit <span class="text-danger">*</span>
        </label>

        <input type="text" class="form-control form-control-thin" id="unit" name="unit" ref="unit" v-model="form.unit"
          placeholder="Unit" />
      </div>

      <div class="mb-4">
        <label class="form-label form-label-thin">Assets</label>

        <MultiSelect v-model="form.asset_ids" :options="assets" placeholder="Select assets…" id="asset_ids"
          name="asset_ids" ref="asset_ids" />
      </div>

      <div class="mb-4">
        <label class="form-label form-label-thin">Vendors</label>

        <MultiSelect v-model="form.vendor_ids" :options="vendors" placeholder="Select vendors…" id="vendor_ids"
          name="vendor_ids" ref="vendor_ids" />
      </div>

      <!-- Description -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Description (If Any)</label>

        <textarea class="form-control form-control-thin" id="description" name="description" ref="description"
          v-model="form.description" placeholder="Material Description" rows="3"></textarea>
      </div>

      <!-- Photo -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Photo</label>

        <div v-if="form.photo_preview" class="mb-2 d-flex align-items-center gap-2">
          <img :src="getFileUrl(form.photo_preview)" alt="Preview" style="max-width: 200px; max-height: 200px;" />

          <div class="text-danger" style="cursor: pointer;" @click="removePhoto">
            <i class="fa fa-times"></i> Remove
          </div>
        </div>

        <input type="file" class="form-control form-control-thin" id="photo" name="photo" ref="photo"
          @change="handlePhotoChange" accept="image/*" />
      </div>

      <!-- File -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Attachment File</label>

        <div v-if="form.existing_file && !form.file"
          class="mb-2 d-flex align-items-center justify-content-between border rounded p-2">
          <a :href="form.existing_file.url" target="_blank" rel="noopener" class="text-dark text-decoration-none small">
            <i class="fa fa-file text-secondary"></i>
            {{ form.existing_file.name }}
          </a>

          <div class="text-danger" style="cursor: pointer;" @click="removeExistingFile">
            <i class="fa fa-times"></i>
          </div>
        </div>

        <input type="file" class="form-control form-control-thin" id="file" name="file" ref="file"
          @change="handleFileChange" />

        <small class="text-muted">Max size: 20MB</small>
      </div>
      <div class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="close">
          <i class="fa fa-times"></i> Cancel
        </button>
        <button type="submit" class="btn btn-primary text-light hover-white btn-md form-submit-btn"
          :disabled="submitting">
          <i class="fa fa-save"></i> {{ submitting ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </template>
  </form>
</template>
<script>
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'

export default {
  name: 'EditMaterial',

  components: {
    MultiSelect
  },

  props: {
    // The material's primary key (DB id), e.g. pass material.id from the list row
    materialId: {
      type: [String, Number],
      required: true
    }
  },

  emits: ['updated', 'close'],

  data() {
    return {
      form: {
        name: '',
        code: '',
        serial_number: '',
        material_category_id: '',
        manufacturer: '',
        unit: '',
        description: '',
        photo: null,            // newly selected File, if replaced
        photo_preview: null,    // existing photo URL, or a data URL preview of a newly selected photo
        remove_photo: false,
        file: null,             // newly selected File, if replaced
        existing_file: null,    // { name, url } of the currently saved attachment, if any
        remove_file: false,
        asset_ids: [],
        vendor_ids: [],
      },

      errors: {},
      submitting: false,
      initialLoading: true,
      materialCategories: [],
      assets: [],
      vendors: [],
    }
  },

  async mounted() {
    this.initialLoading = true
    await Promise.all([
      this.getMaterialCategories(),
      this.getAssets(),
      this.getVendors(),
      this.fetchMaterialDetails()
    ])
    this.initialLoading = false
  },
  watch: {
    materialId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchMaterialDetails()
        }
      }
    }
  },
  methods: {
    handlePhotoChange(event) {
      const file = event.target.files[0]
      if (!file) return

      this.form.photo = file
      this.form.remove_photo = false

      const reader = new FileReader()
      reader.onload = (e) => {
        this.form.photo_preview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    getFileUrl(filePath) {
      if (!filePath) return ''
      if (filePath.startsWith('http')) return filePath

      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      return `${baseUrl}/${filePath}`
    },
    removePhoto() {
      this.form.photo = null
      this.form.photo_preview = null
      this.form.remove_photo = true
      // Clear the native file input so the same file can be re-picked if needed
      const input = document.getElementById('photo')
      if (input) input.value = ''
    },

    handleFileChange(event) {
      const file = event.target.files[0]
      if (!file) return
      this.form.file = file
      this.form.remove_file = false
    },

    removeExistingFile() {
      this.form.existing_file = null
      this.form.remove_file = true
    },

    async handleSubmit() {
      this.submitting = true
      this.errors = {}

      try {
        const fd = new FormData()
        const scalars = [
          'name',
          'code',
          'serial_number',
          'material_category_id',
          'manufacturer',
          'unit',
          'description'
        ]

        scalars.forEach(key => {
          if (this.form[key] !== null && this.form[key] !== '') {
            fd.append(key, this.form[key])
          }
        })

        if (this.form.photo) {
          fd.append('photo', this.form.photo)
        } else if (this.form.remove_photo) {
          fd.append('remove_photo', '1')
        }

        if (this.form.file) {
          fd.append('file', this.form.file)
        } else if (this.form.remove_file) {
          fd.append('remove_file', '1')
        }

        this.form.asset_ids.forEach(id => {
          fd.append('asset_ids[]', id)
        })

        this.form.vendor_ids.forEach(id => {
          fd.append('vendor_ids[]', id)
        })

        const result = await form_api_submit({
          url: `materials/update/${this.materialId}`,
          method: 'POST',
          data: fd,
          curr_form_id: '#material_edit_form'
        })

        if (result.success) {
          this.$emit('created')
          this.$emit('close')
          this.submitting = false
          return
        } else if (result.errors) {
          this.errors = result.errors
        }

      } finally {
        this.submitting = false
      }
    },
    closeEditModal() {
      this.showEditModal = false
      this.editingMaterialId = null
    },
    async fetchMaterialDetails() {
      try {
        const result = await form_api_submit({
          url: `materials/edit/${this.materialId}`,
          method: 'GET',
          show_success_toast: false,
          show_error_toast: true,
        })

        if (result?.success) {
          const material = result.data?.material || result.data || {}

          this.form.name = material.name || ''
          this.form.code = material.code || ''
          this.form.serial_number = material.serial_number || ''
          this.form.material_category_id = material.material_category_id ?? ''
          this.form.manufacturer = material.manufacturer || ''
          this.form.unit = material.unit || ''
          this.form.description = material.description || ''

          this.form.photo_preview = material.photo_url || material.photo || null

          this.form.existing_file = material.file_url || material.file
            ? { name: material.file_name || 'Attachment', url: material.file_url || material.file }
            : null

          this.form.asset_ids = Array.isArray(material.asset_ids) ? material.asset_ids : []
          this.form.vendor_ids = Array.isArray(material.vendor_ids) ? material.vendor_ids : []
        }
      } catch (error) {
        console.error('Failed to load material details', error)
      }
    },

    async getAssets() {
      try {
        const result = await form_api_submit({
          url: 'asset/dropdown',
          method: 'GET',
          show_success_toast: false,
          show_error_toast: false,
        })

        if (result?.success) {
          this.assets = result.data || []
        }
      } catch (error) {
        console.error('Failed to load assets', error)
      }
    },

    async getVendors() {
      try {
        const result = await form_api_submit({
          url: 'vendor/get-vendors',
          method: 'GET',
          show_success_toast: false,
          show_error_toast: false,
        })

        if (result?.success) {
          this.vendors = (result.data || []).map(vendor => ({
            id: vendor.id,
            name: vendor.first_name,
            external_id: vendor.external_id,
          }))
        }
      } catch (error) {
        console.error('Failed to load vendors', error)
      }
    },

    async getMaterialCategories() {
      try {
        const result = await form_api_submit({
          url: 'material-categories/dropdown',
          method: 'GET',
          show_success_toast: false,
          show_error_toast: false,
        })

        if (result?.success) {
          this.materialCategories = result.data || []
        }
      } catch (error) {
        console.error('Failed to load material categories', error)
      }
    },

    close() {
      this.$emit('close')
    },

  }

}
</script>
<style scoped>
.modal-footer {
  padding: 12px 0 0 0;
  border-top: none;
  gap: 8px;
}
</style>